import React, { useEffect, useMemo, useState } from 'react';
import { useGetDashboardQuery, useUpdateDashboardPreferencesMutation } from '../../../../../redux/services/TraderApi';
import {
  type TraderDashboardMetric,
  type TTraderDashboardCurrency,
  type TTraderDashboardPreferences,
} from '../../../../../redux/types/TraderTypes';
import styles from './TraderBalanceBlock.module.css';

const metricOptions: Array<{ key: TraderDashboardMetric; label: string }> = [
  { key: 'balance', label: 'Баланс' },
  { key: 'received', label: 'Получено' },
  { key: 'frozen', label: 'Заморожено' },
  { key: 'requisites', label: 'Реквизиты' },
];

const formatAmount = (value: string | null, scale = 2) => {
  if (value === null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: scale,
    minimumFractionDigits: 0,
  }).format(Number(value));
};
const formatMoney = (value: string | null, code: string, scale = 2) => (
  `${formatAmount(value, scale)} ${code}`
);

const clonePreferences = (preferences: TTraderDashboardPreferences) => ({
  show_currency_block: preferences.show_currency_block !== false,
  currency_codes: [...preferences.currency_codes],
  metrics: { ...preferences.metrics },
});

const CurrencyValue = ({
  currency,
  value,
}: {
  currency: TTraderDashboardCurrency;
  value: string | null;
}) => (
  <div className={styles.currencyValue}>
    <span>{currency.code}</span>
    <strong>{formatMoney(value, currency.code, currency.amount_scale)}</strong>
  </div>
);

export const TraderBalanceBlock = () => {
  const { data, isLoading, isFetching, isError, refetch } = useGetDashboardQuery(undefined, {
    pollingInterval: 10000,
  });
  const [updatePreferences, { isLoading: isSaving }] = useUpdateDashboardPreferencesMutation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState<TTraderDashboardPreferences | null>(null);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (settingsOpen && data?.preferences) setDraft(clonePreferences(data.preferences));
  }, [settingsOpen, data?.preferences]);

  const visibleCurrencies = useMemo(() => {
    if (!data) return [];
    const selected = new Set(data.preferences.currency_codes);
    return data.currencies.filter((currency) => selected.has(currency.code));
  }, [data]);

  const toggleCurrency = (code: string) => {
    if (!draft) return;
    const currencyCodes = draft.currency_codes.includes(code)
      ? draft.currency_codes.filter((item) => item !== code)
      : [...draft.currency_codes, code];
    setDraft({ ...draft, currency_codes: currencyCodes });
  };

  const toggleMetric = (metric: TraderDashboardMetric) => {
    if (!draft) return;
    setDraft({ ...draft, metrics: { ...draft.metrics, [metric]: !draft.metrics[metric] } });
  };

  const saveSettings = async () => {
    if (!draft || !draft.currency_codes.length || !Object.values(draft.metrics).some(Boolean)) return;
    try {
      await updatePreferences(draft).unwrap();
      setSettingsOpen(false);
    } catch {
      setSaveError('Не удалось сохранить настройки. Попробуйте ещё раз.');
    }
  };

  if (isLoading) return <div className={styles.state}>Загружаем балансы…</div>;
  if (isError || !data) {
    return (
      <div className={styles.errorState} role='alert'>
        <span>Не удалось загрузить балансы.</span>
        <button type='button' onClick={refetch}>Повторить</button>
      </div>
    );
  }

  const showCurrencyBlock = data.preferences.show_currency_block !== false;
  const has = data.preferences.metrics;

  return (
    <section className={styles.section} aria-label='Балансы трейдера'>
      <div className={styles.sectionActions}>
        {isFetching && <span>Обновляем…</span>}
        <button type='button' onClick={() => { setSaveError(''); setSettingsOpen(true); }}>
          Настроить вид
        </button>
      </div>
      <div className={styles.wrapper}>
        <div className={styles.block}>
          <h3 className={styles.title}>Баланс</h3>
          <div className={styles.item}><span>USDT</span><strong>{formatMoney(data.balance_usdt, 'USDT', 3)}</strong></div>
          {showCurrencyBlock && has.balance && visibleCurrencies.map((currency) => (
            <CurrencyValue key={currency.code} currency={currency} value={currency.balance_equivalent} />
          ))}
        </div>
        <div className={styles.block}>
          <h3 className={styles.title}>Получено</h3>
          {showCurrencyBlock && has.received ? visibleCurrencies.map((currency) => (
            <CurrencyValue key={currency.code} currency={currency} value={currency.received} />
          )) : <div className={styles.item}>Нет данных</div>}
        </div>
        <div className={styles.block}>
          <h3 className={styles.title}>В работе</h3>
          <div className={styles.item}><span>Заявки</span><strong>{data.active_orders}</strong></div>
          {showCurrencyBlock && has.requisites && visibleCurrencies.map((currency) => (
            <div className={styles.currencyValue} key={currency.code}>
              <span>{currency.code}</span>
              <strong>{currency.active_requisites} рекв.</strong>
            </div>
          ))}
        </div>
        <div className={styles.block}>
          <h3 className={styles.title}>Заморожено</h3>
          <div className={styles.item}><span>USDT</span><strong>{formatMoney(data.frozen_usdt, 'USDT', 3)}</strong></div>
          {showCurrencyBlock && has.frozen && visibleCurrencies.map((currency) => (
            <CurrencyValue key={currency.code} currency={currency} value={currency.frozen} />
          ))}
        </div>
      </div>

      {settingsOpen && draft && (
        <div className={styles.overlay} role='presentation' onMouseDown={() => setSettingsOpen(false)}>
          <div className={styles.dialog} role='dialog' aria-modal='true' onMouseDown={(event) => event.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <h3>Настройка отображения</h3>
              <button type='button' onClick={() => setSettingsOpen(false)} aria-label='Закрыть'>×</button>
            </div>
            <label className={styles.option}>
              <input type='checkbox' checked={draft.show_currency_block} onChange={() => setDraft({ ...draft, show_currency_block: !draft.show_currency_block })} />
              Показывать валюты
            </label>
            <fieldset>
              <legend>Валюты</legend>
              {data.currencies.map((currency) => (
                <label className={styles.option} key={currency.code}>
                  <input type='checkbox' checked={draft.currency_codes.includes(currency.code)} onChange={() => toggleCurrency(currency.code)} />
                  {currency.code} · {currency.name}
                </label>
              ))}
            </fieldset>
            <fieldset>
              <legend>Показатели</legend>
              {metricOptions.map((metric) => (
                <label className={styles.option} key={metric.key}>
                  <input type='checkbox' checked={draft.metrics[metric.key]} onChange={() => toggleMetric(metric.key)} />
                  {metric.label}
                </label>
              ))}
            </fieldset>
            {saveError && <p className={styles.saveError}>{saveError}</p>}
            <div className={styles.dialogFooter}>
              <button type='button' onClick={() => setSettingsOpen(false)}>Отмена</button>
              <button type='button' onClick={saveSettings} disabled={isSaving || !draft.currency_codes.length || !Object.values(draft.metrics).some(Boolean)}>
                {isSaving ? 'Сохраняем…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
